import { useNProgress } from '@vueuse/integrations/useNProgress';

// Single shared NProgress instance for App.vue, the router, and axios.
const { start, done, isLoading, progress } = useNProgress();

let pending = 0;
let doneTimer = null;
let generation = 0;
let watchdogTimer = null;
let hiddenAt = null;

/**
 * If a start never gets a matching done (hung XHR after sleep/disconnect), clear the bar.
 * Must stay above the apiClient timeout (utils/axios.js) so a merely-slow request settles
 * and returns its own token instead of being force-cleared as if it had hung.
 */
const WATCHDOG_MS = 70000;
/** Ignore brief tab switches; only recover after longer away (sleep / background). */
const AWAY_RECOVER_MS = 8000;

function clearWatchdog() {
    if (watchdogTimer) {
        clearTimeout(watchdogTimer);
        watchdogTimer = null;
    }
}

function armWatchdog() {
    clearWatchdog();
    watchdogTimer = setTimeout(() => {
        watchdogTimer = null;
        if (pending > 0) {
            forceDoneProgress();
        }
    }, WATCHDOG_MS);
}

/**
 * Show the top progress bar. Nested calls are counted so the bar stays
 * visible until every matching doneProgress() has run.
 * @returns {number} generation token — pass the same value to doneProgress()
 */
export function startProgress() {
    if (doneTimer) {
        clearTimeout(doneTimer);
        doneTimer = null;
    }
    if (pending === 0) {
        start();
    }
    pending += 1;
    armWatchdog();
    return generation;
}

/**
 * Hide the top progress bar once all in-flight work has finished.
 * A short debounce absorbs sequential request bursts (page boot, etc.)
 * so the bar does not flash between each call.
 * @param {number} [startedGeneration] token from startProgress(); ignored if stale after forceDone
 */
export function doneProgress(startedGeneration) {
    if (startedGeneration !== undefined && startedGeneration !== generation) {
        return;
    }
    pending = Math.max(0, pending - 1);
    if (pending > 0) {
        armWatchdog();
        return;
    }
    clearWatchdog();
    doneTimer = setTimeout(() => {
        doneTimer = null;
        if (pending === 0) {
            done();
        }
    }, 250);
}

/** Force-hide regardless of pending count (navigation error, wake, etc.). */
export function forceDoneProgress() {
    generation += 1;
    pending = 0;
    if (doneTimer) {
        clearTimeout(doneTimer);
        doneTimer = null;
    }
    clearWatchdog();
    done();
}

function recoverStuckProgress() {
    if (pending > 0) {
        forceDoneProgress();
    }
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            hiddenAt = Date.now();
            return;
        }
        const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
        hiddenAt = null;
        if (awayMs >= AWAY_RECOVER_MS) {
            recoverStuckProgress();
        }
    });

    window.addEventListener('online', recoverStuckProgress);

    window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
            recoverStuckProgress();
        }
    });
}

export { isLoading, progress, startProgress as start, doneProgress as done };
